// @ts-nocheck
import React from "react";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  value?: any;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  minDate?: any;
  maxDate?: any;
  disablePast?: boolean;
  disableFuture?: boolean;
  clearable?: boolean;
  className?: string;
  buttonClassName?: string;
  calendarClassName?: string;
  popoverClassName?: string;
  align?: "start" | "center" | "end";
  fromYear?: number;
  toYear?: number;
  name?: string;
  id?: string;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
}

export const toDateOnlyString = (value) => {
  if (!value) return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

export const dateOnlyStringToDate = (value) => {
  const dateOnly = toDateOnlyString(value);
  if (!dateOnly) return undefined;

  const [year, month, day] = dateOnly.split("-").map(Number);
  if (!year || !month || !day) return undefined;

  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export const todayDateOnlyString = () => toDateOnlyString(new Date());

export const formatDateOnly = (value, formatString = "MMM d, yyyy") => {
  const date = dateOnlyStringToDate(value);
  return date ? format(date, formatString) : "";
};

export const compareDateOnly = (left, right) => {
  const leftDate = toDateOnlyString(left);
  const rightDate = toDateOnlyString(right);
  if (!leftDate || !rightDate) return 0;
  return leftDate.localeCompare(rightDate);
};

export const isDateOnlyBefore = (left, right = todayDateOnlyString()) => compareDateOnly(left, right) < 0;

export const isDateOnlyAfter = (left, right = todayDateOnlyString()) => compareDateOnly(left, right) > 0;

export const isDateOnlyBetween = (value, from, to) => {
  if (!value) return false;
  if (from && isDateOnlyBefore(value, from)) return false;
  if (to && isDateOnlyAfter(value, to)) return false;
  return true;
};

export const addDaysToDateOnly = (value, days) => {
  const date = dateOnlyStringToDate(value);
  if (!date) return "";
  date.setDate(date.getDate() + days);
  return toDateOnlyString(date);
};

export default function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled = false,
  required = false,
  minDate,
  maxDate,
  disablePast = false,
  disableFuture = false,
  clearable = true,
  className,
  buttonClassName,
  calendarClassName,
  popoverClassName,
  align = "start",
  fromYear = 1900,
  toYear = 2100,
  name,
  id,
  onBlur,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const dateOnlyValue = toDateOnlyString(value);
  const selectedDate = dateOnlyStringToDate(dateOnlyValue);
  const min = disablePast ? dateOnlyStringToDate(todayDateOnlyString()) : dateOnlyStringToDate(minDate);
  const max = disableFuture ? dateOnlyStringToDate(todayDateOnlyString()) : dateOnlyStringToDate(maxDate);

  const isDisabledDate = (date) => {
    const current = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
    if (min && current < min) return true;
    if (max && current > max) return true;
    return false;
  };

  const handleSelect = (date) => {
    if (!date) {
      if (!required) onChange?.("");
      setOpen(false);
      return;
    }

    onChange?.(toDateOnlyString(date));
    setOpen(false);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onChange?.("");
  };

  return (
    <div className={cn("relative", className)}>
      {name && <input type="hidden" name={name} value={dateOnlyValue} />}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            onBlur={onBlur}
            className={cn(
              "w-full justify-start text-left font-normal bg-white",
              !dateOnlyValue && "text-muted-foreground",
              buttonClassName
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
            <span className="min-w-0 flex-1 truncate">
              {dateOnlyValue ? formatDateOnly(dateOnlyValue) : placeholder}
            </span>
            {clearable && dateOnlyValue && !disabled && !required && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear date"
                onClick={handleClear}
                className="ml-2 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "z-[10000] w-[min(calc(100vw-2rem),22rem)] rounded-lg border border-slate-200 bg-white p-0 shadow-xl shadow-slate-900/12",
            popoverClassName
          )}
          align={align}
          side="bottom"
          sideOffset={6}
          collisionPadding={12}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            disabled={isDisabledDate}
            captionLayout="dropdown-buttons"
            fromYear={fromYear}
            toYear={toYear}
            initialFocus
            className={cn("w-full", calendarClassName)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}